// PortInWizard.jsx
//
// Multi-step modal wizard for porting existing business numbers into SurFox.
//
// Phase B Slice B2: Steps 1-3 (numbers, carrier, account holder).
// Phase C: Step 4 added (LOA e-signature + bill).
// Phase D: Step 4 bill panel activated; Step 5 (Review and submit) added;
//   terminal Submit calls phone-port-in:submit.
// Phase F (2026-05-17): DocuSign integration ripped out. Step 4 now
//   collects only the bill + the authorized representative's name/email
//   that Twilio uses for its own auto-LOA email flow.
//
// State ownership: `usePortInDraft(portRequestId)` owns the draft + autosave.
// This file is pure routing + chrome.

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  ChevronLeft,
  ChevronRight,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ArrowLeftRight,
} from 'lucide-react';
import Step1Numbers from './wizard-steps/Step1Numbers';
import Step2CarrierDetails from './wizard-steps/Step2CarrierDetails';
import Step3AccountHolder from './wizard-steps/Step3AccountHolder';
import Step4BillAndLoa from './wizard-steps/Step4BillAndLoa';
import Step5Review from './wizard-steps/Step5Review';
import { usePortInDraft } from './usePortInDraft';

const STEPS = [
  { key: 1, title: 'Numbers to port' },
  { key: 2, title: 'Carrier details' },
  { key: 3, title: 'Account holder' },
  { key: 4, title: 'Bill and signer' },
  { key: 5, title: 'Review and submit' },
];

const TOTAL_STEPS = STEPS.length;

export default function PortInWizard({ portRequestId, onClose, onCompleted }) {
  const draftState = usePortInDraft(portRequestId);
  const { draft, loading, saving, error, flush } = draftState;
  const [step, setStep] = useState(1);
  const [advancing, setAdvancing] = useState(false);
  const [didAutoJump, setDidAutoJump] = useState(false);

  // When the wizard opens against a port_request that is mid-flight, jump
  // straight to the most useful step. The user is usually here to upload
  // the missing bill or kick off submit, not re-edit earlier-step data.
  // Auto-jump fires ONCE per wizard mount so the user can navigate Back.
  //
  // Phase F: `awaiting_signature` / `awaiting_documents` no longer happen
  // pre-submit because the SurFox-side DocuSign step is gone. The only
  // pre-submit statuses now are `draft` (wizard mid-flight) and
  // `ready_to_submit` (bill uploaded, ready for Step 5 Submit).
  useEffect(() => {
    if (didAutoJump) return;
    if (!draft) return;
    if (draft.status === 'ready_to_submit') {
      setStep(5);
      setDidAutoJump(true);
    } else if (draft.status === 'draft') {
      setDidAutoJump(true);
    }
  }, [draft, didAutoJump]);

  // When the user clicks Continue, flush autosave first so we never leave the
  // current step's edits in a pending-debounce window during navigation.
  const goNext = async () => {
    if (step >= TOTAL_STEPS) return;
    setAdvancing(true);
    try {
      await flush();
    } finally {
      setAdvancing(false);
      setStep((s) => Math.min(TOTAL_STEPS, s + 1));
    }
  };

  const goBack = () => {
    if (step <= 1) return;
    setStep((s) => Math.max(1, s - 1));
  };

  const handleClose = async () => {
    try {
      await flush();
    } finally {
      onClose?.();
    }
  };

  // Enable Continue only when the current step's minimum requirements are met
  const canProceed = (() => {
    if (!draft) return false;
    if (step === 1) {
      const portable = (draft.numbers ?? []).filter(
        (n) => n?.portability?.portable === true
      );
      return portable.length >= 1;
    }
    if (step === 2) {
      // Every portable number must have account_pin_set, account_number_set,
      // billing_zip, and losing_carrier set.
      const portable = (draft.numbers ?? []).filter(
        (n) => n?.portability?.portable === true
      );
      if (portable.length === 0) return false;
      return portable.every(
        (n) =>
          n.account_pin_set === true &&
          n.account_number_set === true &&
          n.billing_zip &&
          /^\d{5}(-\d{4})?$/.test(String(n.billing_zip)) &&
          n.losing_carrier
      );
    }
    if (step === 3) {
      // Every portable number must have an account_holder_name plus a
      // service_address with the minimum required components. A loose address
      // check (presence of street1, city, state, zip) keeps Step 3 advancing
      // even when Google Places is not configured and the customer typed
      // manually. Phase D: also require losing_carrier_customer_type so the
      // Twilio body builder has everything for losing_carrier_information.
      const portable = (draft.numbers ?? []).filter(
        (n) => n?.portability?.portable === true
      );
      if (portable.length === 0) return false;
      if (!['Individual', 'Business'].includes(draft.losing_carrier_customer_type)) {
        return false;
      }
      return portable.every((n) => {
        const addr = n.service_address;
        return (
          typeof n.account_holder_name === 'string' &&
          n.account_holder_name.trim().length > 0 &&
          addr &&
          typeof addr.street1 === 'string' && addr.street1.trim().length > 0 &&
          typeof addr.city === 'string' && addr.city.trim().length > 0 &&
          typeof addr.state === 'string' && addr.state.trim().length > 0 &&
          typeof addr.zip === 'string' && /^\d{5}(-\d{4})?$/.test(addr.zip.trim())
        );
      });
    }
    if (step === 4) {
      // Phase F: Step 4 is now bill upload + authorized-representative
      // name/email. Continue allowed when the bill is uploaded AND both
      // signer fields are populated (Twilio needs them for its auto-LOA
      // email flow).
      const billUploaded = !!draft.bill_twilio_document_sid;
      const signerNameValid =
        typeof draft.signer_name === 'string' &&
        draft.signer_name.trim().length > 0;
      const signerEmailValid =
        typeof draft.signer_email === 'string' &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.signer_email.trim());
      return billUploaded && signerNameValid && signerEmailValid;
    }
    if (step === 5) {
      return false; // terminal — submit lives in Step 5 itself
    }
    return false;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <AnimatePresence>
        <motion.div
          key="port-in-wizard"
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          transition={{ duration: 0.18 }}
          className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-orange-100 rounded-lg flex items-center justify-center">
                <ArrowLeftRight className="w-4 h-4 text-orange-500" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-slate-900">Port your existing numbers</h2>
                <p className="text-xs text-slate-400">
                  Step {step} of {TOTAL_STEPS}. {STEPS[step - 1]?.title}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <SaveIndicator saving={saving} error={error} />
              <button
                onClick={handleClose}
                type="button"
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                aria-label="Close wizard"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-slate-100">
            <div
              className="h-1 bg-orange-500 transition-all duration-300"
              style={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
            />
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            {loading && (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
              </div>
            )}

            {!loading && error && !draft && (
              <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {!loading && draft && (
              <>
                {step === 1 && <Step1Numbers draftState={draftState} />}
                {step === 2 && <Step2CarrierDetails draftState={draftState} />}
                {step === 3 && <Step3AccountHolder draftState={draftState} />}
                {step === 4 && <Step4BillAndLoa draftState={draftState} />}
                {step === 5 && (
                  <Step5Review
                    draftState={draftState}
                    onJumpToStep={(n) => setStep(Math.max(1, Math.min(TOTAL_STEPS, n)))}
                    onSubmitted={() => {
                      onCompleted?.();
                      handleClose();
                    }}
                  />
                )}
              </>
            )}
          </div>

          {/* Footer nav */}
          <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex items-center justify-between rounded-b-2xl">
            <button
              onClick={goBack}
              disabled={step === 1}
              type="button"
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-slate-500 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>

            {step < TOTAL_STEPS && (
              <button
                onClick={goNext}
                disabled={!canProceed || advancing}
                type="button"
                className="flex items-center gap-1.5 px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {advancing ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Continue
                <ChevronRight className="w-4 h-4" />
              </button>
            )}

            {step === TOTAL_STEPS && (
              <span className="text-xs text-slate-400">
                Submit lives in the panel above.
              </span>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

function SaveIndicator({ saving, error }) {
  if (error) {
    return (
      <span className="hidden sm:inline-flex items-center gap-1 text-xs text-red-600">
        <AlertCircle className="w-3.5 h-3.5" />
        Save failed
      </span>
    );
  }
  if (saving) {
    return (
      <span className="hidden sm:inline-flex items-center gap-1 text-xs text-slate-500">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        Saving
      </span>
    );
  }
  return (
    <span className="hidden sm:inline-flex items-center gap-1 text-xs text-emerald-600">
      <CheckCircle2 className="w-3.5 h-3.5" />
      Saved
    </span>
  );
}
